import jsPDF from "jspdf";
import { AccountBreakdown } from "../core/_requests_v2";
import { getStrategicContextSections, getAssumptionItems, richTextToPlain } from "./reportContent";

// ---- Types ----

export interface AccountWithValues extends AccountBreakdown {
    highIntentConversions: number;
    generalConversions: number;
    totalConversions: number;
    revenue: number;
    profit: number;
    preneedPct: number;
}

interface ProjectionDataPoint {
    cumulativeSignatures: number;
    cumulativeConversions: number;
    cumulativeRevenue: number;
    cumulativeProfit: number;
}

interface SensitivityData {
    closeRates: number[];
    generalRates: number[];
    margins: number[];
    grid: { closeRate: number; generalRate: number; margin: number; profit: number }[];
}

interface BaselineData {
    account: AccountBreakdown;
    monthsInRange: number;
    signaturesPerMonth: number;
    preneedRate: number;
}

export interface ReportData {
    baseline: BaselineData;
    finalTotals: ProjectionDataPoint;
    sensitivityData: SensitivityData;
    chartImageData: string | null;
    accountTableData: AccountWithValues[];
    targetAccounts: number;
    projectionYears: number;
    adoptionRamp: number;
    closeRate: number;
    generalConversion: number;
    policyValue: number;
    insuranceMargin: number;
    collectionRate: number;
    startDate: string;
    endDate: string;
    totalAccounts?: number;
    totalSignatures?: number;
}

// ---- Helpers ----

function formatLargeNumber(value: number): string {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${Math.round(value / 1_000).toLocaleString()}K`;
    return `$${value.toLocaleString()}`;
}

function formatCount(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${Math.round(value / 1_000).toLocaleString()}K`;
    return value.toLocaleString();
}

// ---- Colors ----
const COLORS = {
    green: [22, 163, 74] as [number, number, number],      // #16a34a
    blue: [37, 99, 235] as [number, number, number],        // #2563eb
    purple: [147, 51, 234] as [number, number, number],     // #9333ea
    amber: [180, 83, 9] as [number, number, number],        // #b45309
    darkGray: [55, 65, 81] as [number, number, number],     // #374151
    medGray: [107, 114, 128] as [number, number, number],   // #6b7280
    lightGray: [229, 231, 235] as [number, number, number], // #e5e7eb
    veryLightGray: [249, 250, 251] as [number, number, number], // #f9fafb
    white: [255, 255, 255] as [number, number, number],
    black: [0, 0, 0] as [number, number, number],
    highlightBg: [220, 252, 231] as [number, number, number], // green-100
    headerBg: [243, 244, 246] as [number, number, number],    // gray-100
    blueBg: [239, 246, 255] as [number, number, number],      // blue-50
};

// ---- Page dimensions (landscape letter) ----
const PAGE_W = 279.4; // mm
const PAGE_H = 215.9; // mm
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

// ---- Drawing helpers ----

function drawPageHeader(pdf: jsPDF, pageTitle: string) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text("Signature Guestbook", MARGIN, 10);
    pdf.setFont("helvetica", "bold");
    pdf.text(pageTitle, PAGE_W / 2, 10, { align: "center" });
    pdf.setFont("helvetica", "normal");
}

function drawPageFooter(pdf: jsPDF, pageNum: number, totalPages: number, dateStr: string) {
    const y = PAGE_H - 8;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text(dateStr, MARGIN, y);
    pdf.text(`Page ${pageNum} of ${totalPages}`, PAGE_W / 2, y, { align: "center" });
    pdf.text("Confidential", PAGE_W - MARGIN, y, { align: "right" });
}

function drawRect(pdf: jsPDF, x: number, y: number, w: number, h: number, fill: [number, number, number], border?: [number, number, number]) {
    pdf.setFillColor(...fill);
    if (border) {
        pdf.setDrawColor(...border);
        pdf.setLineWidth(0.3);
        pdf.rect(x, y, w, h, "FD");
    } else {
        pdf.rect(x, y, w, h, "F");
    }
}

function drawTableRow(
    pdf: jsPDF,
    cols: { text: string; width: number; align?: "left" | "right" | "center" }[],
    x: number,
    y: number,
    rowHeight: number,
    bgColor?: [number, number, number],
    textColor?: [number, number, number],
    isBold?: boolean,
    borderColor?: [number, number, number],
) {
    const bColor = borderColor || COLORS.lightGray;
    let cx = x;
    for (const col of cols) {
        if (bgColor) {
            drawRect(pdf, cx, y, col.width, rowHeight, bgColor);
        }
        // Cell border
        pdf.setDrawColor(...bColor);
        pdf.setLineWidth(0.2);
        pdf.rect(cx, y, col.width, rowHeight, "S");

        // Text
        pdf.setFont("helvetica", isBold ? "bold" : "normal");
        pdf.setTextColor(...(textColor || COLORS.darkGray));
        const textY = y + rowHeight / 2 + 1.2;
        const padding = 3;
        if (col.align === "right") {
            pdf.text(col.text, cx + col.width - padding, textY, { align: "right" });
        } else if (col.align === "center") {
            pdf.text(col.text, cx + col.width / 2, textY, { align: "center" });
        } else {
            pdf.text(col.text, cx + padding, textY);
        }
        cx += col.width;
    }
}

// ---- Main export ----

export function generateProjectionReport(data: ReportData): void {
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const totalPages = 7;

    // ===== PAGE 1: Cover + Executive Summary =====
    drawPageHeader(pdf, "Executive Summary");

    // Title
    let y = 22;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Preneed Insurance Onboarding Projection", PAGE_W / 2, y, { align: "center" });

    y += 8;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text(`Signature Guestbook  |  Generated ${dateStr}`, PAGE_W / 2, y, { align: "center" });

    // Assumptions box
    y += 10;
    const boxH = 18;
    drawRect(pdf, MARGIN, y, CONTENT_W, boxH, COLORS.blueBg, COLORS.lightGray);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("ASSUMPTIONS", MARGIN + 4, y + 5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);

    const assumptions = [
        `Target: ${data.targetAccounts.toLocaleString()} accounts`,
        `Period: ${data.projectionYears} years`,
        `Close Rate: ${data.closeRate}%`,
        `General Conv: ${data.generalConversion}%`,
        `Policy Value: $${data.policyValue.toLocaleString()}`,
        `Collection: ${data.collectionRate}%`,
        `Margin: ${data.insuranceMargin}%`,
        data.adoptionRamp > 0 ? `Ramp: ${data.adoptionRamp}mo` : null,
    ].filter(Boolean) as string[];

    pdf.text(assumptions.join("   |   "), MARGIN + 4, y + 12);

    // Executive summary paragraphs
    y += boxH + 8;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Executive Summary", MARGIN, y);

    y += 7;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.darkGray);

    const effectivePremium = Math.round(data.policyValue * data.collectionRate / 100);
    const profitPerPolicy = Math.round(effectivePremium * (data.insuranceMargin / 100));

    const para1 = `This projection models the insurance revenue opportunity from onboarding ${data.targetAccounts.toLocaleString()} funeral homes onto the Signature Guestbook platform over a ${data.projectionYears}-year period. Using our median account (${data.baseline.account.account_name}) as a baseline — which produces ${data.baseline.signaturesPerMonth.toFixed(1)} guestbook signatures per month with a ${(data.baseline.preneedRate * 100).toFixed(1)}% preneed interest rate — we project the cumulative value of converting those signatures into preneed insurance policies.`;

    const para2 = `At a ${data.closeRate}% close rate on high-intent leads, ${data.generalConversion}% general conversion, $${data.policyValue.toLocaleString()} policy face value (${data.collectionRate}% collection rate = $${effectivePremium.toLocaleString()} effective premium), and ${data.insuranceMargin}% insurance margin ($${profitPerPolicy.toLocaleString()} profit per policy), the model projects ${formatLargeNumber(data.finalTotals.cumulativeProfit)} in total profit from ${formatLargeNumber(data.finalTotals.cumulativeRevenue)} in premium revenue across ${formatCount(data.finalTotals.cumulativeConversions)} policies.`;

    const para3 = `The onboarding follows an S-curve (sigmoid) adoption model reflecting realistic enterprise SaaS rollout dynamics — starting slow, accelerating through the middle period, and plateauing as the target account base fills.${data.adoptionRamp > 0 ? ` Each new account ramps to full output over ${data.adoptionRamp} months.` : ""} The sensitivity analysis on page 4 shows how these numbers respond to different close rate, conversion, and margin assumptions.`;

    const lineH = 4;
    const maxW = CONTENT_W;

    for (const para of [para1, para2, para3]) {
        const lines = pdf.splitTextToSize(para, maxW);
        pdf.text(lines, MARGIN, y);
        y += lines.length * lineH + 3;
    }

    // Key metrics row
    y += 2;
    const metricW = CONTENT_W / 4;
    const metricH = 22;
    const metrics = [
        { label: "TOTAL PROJECTED PROFIT", value: formatLargeNumber(data.finalTotals.cumulativeProfit), color: COLORS.green, sub: `${data.projectionYears}-year at ${data.insuranceMargin}% margin` },
        { label: "TOTAL PREMIUM REVENUE", value: formatLargeNumber(data.finalTotals.cumulativeRevenue), color: COLORS.blue, sub: `${formatCount(data.finalTotals.cumulativeConversions)} policies x $${effectivePremium.toLocaleString()} effective` },
        { label: "PROJECTED POLICIES", value: formatCount(data.finalTotals.cumulativeConversions), color: COLORS.purple, sub: `${data.closeRate}% preneed + ${data.generalConversion}% general` },
        { label: "TOTAL SIGNATURES", value: formatCount(data.finalTotals.cumulativeSignatures), color: COLORS.amber, sub: `${data.targetAccounts.toLocaleString()} accounts over ${data.projectionYears}yr` },
    ];

    for (let i = 0; i < metrics.length; i++) {
        const mx = MARGIN + i * metricW;
        drawRect(pdf, mx + 1, y, metricW - 2, metricH, COLORS.veryLightGray, COLORS.lightGray);

        // Color accent bar on left
        pdf.setFillColor(...metrics[i].color);
        pdf.rect(mx + 1, y, 1.5, metricH, "F");

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6.5);
        pdf.setTextColor(...COLORS.medGray);
        pdf.text(metrics[i].label, mx + 6, y + 5.5);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.setTextColor(...metrics[i].color);
        pdf.text(metrics[i].value, mx + 6, y + 14);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6.5);
        pdf.setTextColor(...COLORS.medGray);
        pdf.text(metrics[i].sub, mx + 6, y + 19);
    }

    drawPageFooter(pdf, 1, totalPages, dateStr);

    // ===== PAGE 2: Strategic Context =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Strategic Context");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Strategic Context: Why Stylus-Based Digital Guestbooks", MARGIN, y);

    y += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.darkGray);

    const strategySections = getStrategicContextSections();

    for (const section of strategySections) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(...COLORS.darkGray);
        pdf.text(section.title, MARGIN, y);
        y += 5;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...COLORS.medGray);
        const sectionText = section.body.map(para => richTextToPlain(para)).join(' ');
        const lines = pdf.splitTextToSize(sectionText, CONTENT_W);
        pdf.text(lines, MARGIN, y);
        y += lines.length * 3.8 + 6;
    }

    drawPageFooter(pdf, 2, totalPages, dateStr);

    // ===== PAGE 3: Account Performance Table =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Account Performance");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Current Account Performance", MARGIN, y);

    y += 6;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text(
        `Baseline: ${data.baseline.account.account_name} (median) | ${data.baseline.signaturesPerMonth.toFixed(1)} sigs/mo | ${(data.baseline.preneedRate * 100).toFixed(1)}% preneed rate | ${data.baseline.monthsInRange.toFixed(0)} months of data | Data period: ${data.startDate} to ${data.endDate}`,
        MARGIN,
        y,
    );

    y += 4;
    if (data.totalAccounts && data.totalSignatures) {
        pdf.text(
            `Showing top ${data.accountTableData.length} of ${data.totalAccounts.toLocaleString()} active accounts (${data.totalSignatures.toLocaleString()} total signatures)`,
            MARGIN,
            y,
        );
        y += 4;
    }

    y += 2;

    // Table columns
    const colWidths = [12, 60, 22, 28, 28, 28, 32, 32];
    const totalColW = colWidths.reduce((a, b) => a + b, 0);
    const tableX = MARGIN + (CONTENT_W - totalColW) / 2; // center the table
    const rowH = 7;

    // Header row
    const headers = ["#", "Account", "Services", "Signatures", "Preneed", "Proj. Policies", "Revenue", "Profit"];
    const headerCols = headers.map((h, i) => ({
        text: h,
        width: colWidths[i],
        align: (i <= 1 ? "left" : "right") as "left" | "right",
    }));

    pdf.setFontSize(7);
    drawTableRow(pdf, headerCols, tableX, y, rowH, COLORS.headerBg, COLORS.medGray, true);
    y += rowH;

    // Data rows
    pdf.setFontSize(7);
    const maxRows = Math.min(data.accountTableData.length, 30); // Limit to avoid overflow
    for (let i = 0; i < maxRows; i++) {
        const row = data.accountTableData[i];
        const bgColor = i % 2 === 0 ? COLORS.white : COLORS.veryLightGray;

        const cols = [
            { text: String(i + 1), width: colWidths[0], align: "left" as const },
            { text: row.account_name.length > 28 ? row.account_name.substring(0, 26) + "..." : row.account_name, width: colWidths[1], align: "left" as const },
            { text: row.service_count.toLocaleString(), width: colWidths[2], align: "right" as const },
            { text: row.signature_count.toLocaleString(), width: colWidths[3], align: "right" as const },
            { text: `${row.preneed_count.toLocaleString()} (${row.preneedPct.toFixed(1)}%)`, width: colWidths[4], align: "right" as const },
            { text: row.totalConversions.toLocaleString(), width: colWidths[5], align: "right" as const },
            { text: formatLargeNumber(row.revenue), width: colWidths[6], align: "right" as const },
            { text: formatLargeNumber(row.profit), width: colWidths[7], align: "right" as const },
        ];

        drawTableRow(pdf, cols, tableX, y, rowH, bgColor, COLORS.darkGray, false);
        y += rowH;
    }

    // Totals row
    const totalRow = data.accountTableData.reduce(
        (acc, r) => ({
            services: acc.services + r.service_count,
            sigs: acc.sigs + r.signature_count,
            preneed: acc.preneed + r.preneed_count,
            conversions: acc.conversions + r.totalConversions,
            revenue: acc.revenue + r.revenue,
            profit: acc.profit + r.profit,
        }),
        { services: 0, sigs: 0, preneed: 0, conversions: 0, revenue: 0, profit: 0 },
    );
    const totalPreneedPct = totalRow.sigs > 0 ? (totalRow.preneed / totalRow.sigs * 100) : 0;

    const totalCols = [
        { text: "", width: colWidths[0], align: "left" as const },
        { text: `Total (${data.accountTableData.length} accounts)`, width: colWidths[1], align: "left" as const },
        { text: totalRow.services.toLocaleString(), width: colWidths[2], align: "right" as const },
        { text: totalRow.sigs.toLocaleString(), width: colWidths[3], align: "right" as const },
        { text: `${totalRow.preneed.toLocaleString()} (${totalPreneedPct.toFixed(1)}%)`, width: colWidths[4], align: "right" as const },
        { text: totalRow.conversions.toLocaleString(), width: colWidths[5], align: "right" as const },
        { text: formatLargeNumber(totalRow.revenue), width: colWidths[6], align: "right" as const },
        { text: formatLargeNumber(totalRow.profit), width: colWidths[7], align: "right" as const },
    ];

    drawTableRow(pdf, totalCols, tableX, y, rowH + 1, COLORS.headerBg, COLORS.darkGray, true);

    drawPageFooter(pdf, 3, totalPages, dateStr);

    // ===== PAGE 4: Projection Chart + Explanation =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Onboarding Projection");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Onboarding Projection", MARGIN, y);

    y += 4;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text(
        `${data.targetAccounts.toLocaleString()} funeral homes over ${data.projectionYears} years | S-curve adoption model${data.adoptionRamp > 0 ? ` | ${data.adoptionRamp}-month ramp per account` : ""}`,
        MARGIN,
        y,
    );

    // Chart image
    if (data.chartImageData) {
        y += 4;
        const chartW = CONTENT_W - 10;
        const chartH = 100;
        const chartX = MARGIN + (CONTENT_W - chartW) / 2;
        pdf.addImage(data.chartImageData, "PNG", chartX, y, chartW, chartH);
        y += chartH + 6;
    } else {
        y += 10;
        pdf.setFontSize(10);
        pdf.setTextColor(...COLORS.medGray);
        pdf.text("[Chart not available]", PAGE_W / 2, y, { align: "center" });
        y += 10;
    }

    // Condensed explanation
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("How This Works", MARGIN, y);
    y += 5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);

    const explanationLines = pdf.splitTextToSize(
        `We use the median performing funeral home (${data.baseline.account.account_name}: ${Math.round(data.baseline.signaturesPerMonth)} signatures/month) as a realistic baseline for what one account produces. ` +
        `Imagining ${data.targetAccounts.toLocaleString()} funeral homes gradually joining over ${data.projectionYears} years via an S-curve rollout, each signature represents a funeral attendee. ` +
        `${(data.baseline.preneedRate * 100).toFixed(1)}% express preneed interest; we estimate ${data.closeRate}% of those convert to a preneed whole-life policy with a $${data.policyValue.toLocaleString()} face value. ` +
        `A ${data.collectionRate}% collection rate accounts for the mix of single-premium and installment buyers (with lapse risk), yielding $${effectivePremium.toLocaleString()} effective premium per policy. ` +
        `The remaining signatures convert at ${data.generalConversion}%. ` +
        `At ${data.insuranceMargin}% margin (a proxy for total per-policy economic return), ${data.targetAccounts.toLocaleString()} accounts produce ${formatLargeNumber(data.finalTotals.cumulativeProfit)} profit from ${formatLargeNumber(data.finalTotals.cumulativeRevenue)} revenue over the period.`,
        CONTENT_W,
    );
    pdf.text(explanationLines, MARGIN, y);

    drawPageFooter(pdf, 4, totalPages, dateStr);

    // ===== PAGE 5: Sensitivity Analysis =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Sensitivity Analysis");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Sensitivity Analysis", MARGIN, y);

    y += 6;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text(
        `${data.projectionYears}-year projected profit under different assumption combinations. Current assumptions are highlighted.${data.adoptionRamp > 0 ? ` Includes ${data.adoptionRamp}-month per-account adoption ramp.` : ""}`,
        MARGIN,
        y,
    );

    y += 8;

    // One table per margin level
    const sensColWidths = [55, 35, 35, 35, 35];
    const sensRowH = 7;
    const sensTableW = sensColWidths.reduce((a, b) => a + b, 0);

    for (let mi = 0; mi < data.sensitivityData.margins.length; mi++) {
        const mg = data.sensitivityData.margins[mi];
        const isCurrMargin = mg === data.insuranceMargin;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...COLORS.darkGray);
        const marginLabel = `At ${mg}% Insurance Margin` + (isCurrMargin ? "  (current)" : "");
        pdf.text(marginLabel, MARGIN, y);
        y += 4;

        // Header
        const sensHeaders = [
            { text: "Close Rate \u2193 / General Conv. \u2192", width: sensColWidths[0], align: "left" as const },
            ...data.sensitivityData.generalRates.map((gr, i) => ({
                text: `${gr}%`,
                width: sensColWidths[i + 1],
                align: "right" as const,
            })),
        ];

        pdf.setFontSize(7);
        drawTableRow(pdf, sensHeaders, MARGIN, y, sensRowH, COLORS.headerBg, COLORS.medGray, true);
        y += sensRowH;

        // Rows
        for (const cr of data.sensitivityData.closeRates) {
            const cols: { text: string; width: number; align: "left" | "right" | "center" }[] = [
                { text: `${cr}% close`, width: sensColWidths[0], align: "left" },
            ];

            for (let gi = 0; gi < data.sensitivityData.generalRates.length; gi++) {
                const gr = data.sensitivityData.generalRates[gi];
                const cell = data.sensitivityData.grid.find(
                    (g) => g.closeRate === cr && g.generalRate === gr && g.margin === mg,
                );
                cols.push({
                    text: cell ? formatLargeNumber(cell.profit) : "\u2014",
                    width: sensColWidths[gi + 1],
                    align: "right",
                });
            }

            // Check if this row contains the current assumption
            const isCurrentRow = cr === data.closeRate && isCurrMargin;
            drawTableRow(
                pdf,
                cols,
                MARGIN,
                y,
                sensRowH,
                isCurrentRow ? COLORS.blueBg : COLORS.white,
                isCurrentRow ? COLORS.blue : COLORS.darkGray,
                false,
            );

            // Highlight the exact current cell
            if (isCurrMargin && cr === data.closeRate) {
                const grIdx = data.sensitivityData.generalRates.indexOf(data.generalConversion);
                if (grIdx >= 0) {
                    const cellX = MARGIN + sensColWidths.slice(0, grIdx + 1).reduce((a, b) => a + b, 0);
                    const cellW = sensColWidths[grIdx + 1];
                    drawRect(pdf, cellX, y, cellW, sensRowH, COLORS.highlightBg);
                    // Re-draw border
                    pdf.setDrawColor(...COLORS.green);
                    pdf.setLineWidth(0.5);
                    pdf.rect(cellX, y, cellW, sensRowH, "S");
                    // Re-draw text
                    const cell = data.sensitivityData.grid.find(
                        (g) => g.closeRate === cr && g.generalRate === data.generalConversion && g.margin === mg,
                    );
                    if (cell) {
                        pdf.setFont("helvetica", "bold");
                        pdf.setFontSize(7);
                        pdf.setTextColor(...COLORS.green);
                        pdf.text(formatLargeNumber(cell.profit), cellX + cellW - 3, y + sensRowH / 2 + 1.2, { align: "right" });
                    }
                }
            }

            y += sensRowH;
        }

        y += 8;
    }

    drawPageFooter(pdf, 5, totalPages, dateStr);

    // ===== PAGE 6: Assumptions & Validation =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Assumptions & Validation");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Assumptions & Validation", MARGIN, y);

    y += 6;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text("What is observed data vs. what is assumed — and the reasoning behind each assumption.", MARGIN, y);

    y += 8;

    const statusColorMap: Record<string, [number, number, number]> = {
        'KEY LIMITATION': [220, 38, 38],
        'ASSUMED': COLORS.amber,
        'OBSERVED': COLORS.green,
        'QUALITATIVE': COLORS.medGray,
    };

    const validationItems = getAssumptionItems({
        closeRate: data.closeRate,
        generalConversion: data.generalConversion,
        policyValue: data.policyValue,
        insuranceMargin: data.insuranceMargin,
        collectionRate: data.collectionRate,
        preneedRate: data.baseline.preneedRate,
        accountName: data.baseline.account.account_name,
        monthsInRange: data.baseline.monthsInRange,
        signaturesPerMonth: data.baseline.signaturesPerMonth,
        targetAccounts: data.targetAccounts,
    });

    for (const item of validationItems) {
        const statusColor = statusColorMap[item.status] || COLORS.medGray;

        // Status label + accent bar
        pdf.setFillColor(...statusColor);
        pdf.rect(MARGIN, y - 1, 1.5, 3.5, "F");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6);
        pdf.setTextColor(...statusColor);
        pdf.text(item.status, MARGIN + 4, y + 1.5);

        const statusWidth = pdf.getTextWidth(item.status);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...COLORS.darkGray);
        pdf.text(item.title, MARGIN + 4 + statusWidth + 3, y + 1.5);

        y += 5;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...COLORS.medGray);
        const bodyText = item.body.map(para => richTextToPlain(para)).join(' ');
        const calloutText = item.callout ? ' ' + item.callout.map(para => richTextToPlain(para)).join(' ') : '';
        const fullText = bodyText + calloutText;
        const lines = pdf.splitTextToSize(fullText, CONTENT_W - 6);
        pdf.text(lines, MARGIN + 4, y);
        y += lines.length * 3.2 + 4;
    }

    drawPageFooter(pdf, 6, totalPages, dateStr);

    // ===== PAGE 7: Methodology =====
    pdf.addPage("letter", "landscape");
    drawPageHeader(pdf, "Methodology");

    y = 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...COLORS.darkGray);
    pdf.text("Methodology", MARGIN, y);

    y += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.darkGray);

    const methodSections = [
        {
            title: "Onboarding Model",
            text: `The projection uses an S-curve (sigmoid) onboarding model: f(t) = N / (1 + e^(-k(t - t0))) where k = ln(49) / midpoint, producing approximately 2% activation at month 0 and 98% at period end. This reflects typical enterprise SaaS rollout dynamics where adoption starts slow, accelerates through the midpoint, and plateaus as the target account base fills.${data.adoptionRamp > 0 ? ` Each newly onboarded account linearly ramps from 0% to 100% output over ${data.adoptionRamp} months, reflecting real-world adoption friction where new accounts take time to fully integrate the guestbook system into their service workflow.` : ""}`,
        },
        {
            title: "Per-Account Output",
            text: `Per-account output is derived from the median account's observed monthly signature rate (${data.baseline.signaturesPerMonth.toFixed(1)} signatures/month from ${data.baseline.account.account_name} over ${data.baseline.monthsInRange.toFixed(0)} months of data). Using the median (rather than mean) provides a conservative, outlier-resistant baseline that better represents a typical funeral home's throughput.`,
        },
        {
            title: "Conversion Model",
            text: `Each signature represents a funeral attendee. Of all signatures, ${(data.baseline.preneedRate * 100).toFixed(1)}% express preneed interest (high-intent leads). These convert at a ${data.closeRate}% close rate. The remaining signatures (general attendees) convert at a ${data.generalConversion}% baseline rate, capturing the smaller but still material opportunity from the broader audience. Total conversions = (preneed signatures x close rate) + (general signatures x general conversion rate).`,
        },
        {
            title: "Policy Value & Profit",
            text: `Policy face value ($${data.policyValue.toLocaleString()}) is based on LIMRA's 2024 survey average ($5,398) [LIMRA 2024]. Preneed policies can be paid as a single lump sum or in monthly installments over 5-10 years. A ${data.collectionRate}% collection rate accounts for the blended payment mix and installment lapse risk, yielding an effective premium of $${effectivePremium.toLocaleString()} per policy. The insurance company collects premiums well before paying the death benefit years or decades later, investing the funds in the interim. Profit = revenue x ${data.insuranceMargin}% margin (a simplified proxy for total per-policy economic return including investment income and lapse gains; published company-level margins are lower).`,
        },
        {
            title: "Sensitivity Analysis",
            text: `The sensitivity analysis varies close rate (20%, 35%, 50%), general conversion (0.5%, 1%, 2%, 3%), and insurance margin (15%, 25%, 35%) independently to show the profit range across plausible assumption sets. The current assumptions are highlighted in each table. This demonstrates the robustness of the opportunity — even conservative assumptions produce meaningful profit numbers at scale.`,
        },
    ];

    for (const section of methodSections) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...COLORS.darkGray);
        pdf.text(section.title, MARGIN, y);
        y += 5;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(...COLORS.medGray);
        const lines = pdf.splitTextToSize(section.text, CONTENT_W);
        pdf.text(lines, MARGIN, y);
        y += lines.length * 3.5 + 5;
    }

    // Confidential footer
    y = PAGE_H - 24;
    pdf.setDrawColor(...COLORS.lightGray);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 6;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.medGray);
    pdf.text("Confidential — Signature Guestbook", PAGE_W / 2, y, { align: "center" });
    y += 4;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text("This document contains proprietary projections and should not be distributed without authorization.", PAGE_W / 2, y, { align: "center" });

    drawPageFooter(pdf, 7, totalPages, dateStr);

    // ===== Save =====
    const today = new Date().toISOString().split("T")[0];
    const filename = `onboarding-projection-${data.targetAccounts}-accounts-${data.projectionYears}yr-${today}.pdf`;
    pdf.save(filename);
}
